"use client";

import { ChevronDown, Phone } from "lucide-react";
import { Icon } from "@/components/ui/Icon";
import {
  PHONE_COUNTRIES,
  countryOf,
  reconcileTyped,
  type CountryCode,
  type PhoneValue,
} from "./phone-countries";

/**
 * The phone input used by signup and by both WhatsApp pairing steps.
 *
 * The country chip is a real PICKER, not decoration. It used to be a static
 * `🇬🇧 +44` span, which meant a non-UK number was not refused — it was
 * quietly rewritten into a UK one nobody could ring.
 *
 * The pure half (assembly, splitting, validation) lives in ./phone-countries
 * so it can be tested and called without pulling a client component in.
 */
interface PhoneFieldProps {
  value: PhoneValue;
  onChange: (v: PhoneValue) => void;
  error?: string | null;
}

export function PhoneField({ value, onChange, error }: PhoneFieldProps) {
  const country = countryOf(value.country);

  return (
    <div className="mucka-auth-field">
      <div className="mucka-auth-phone-row">
        {/* The chevron is not decoration. `.mucka-auth-cc` carries
            `appearance: none`, so without it a select renders as a flat chip
            indistinguishable from the static span this replaced — a control
            nobody can tell is a control is the bug, one layer up. */}
        <div className="mucka-auth-cc-wrap">
          <select
            className="mucka-auth-cc"
            aria-label="Country code"
            value={value.country}
            onChange={(e) =>
              // Only the country moves. Retyping the number because they picked
              // the wrong flag first is exactly the re-entry anti-pattern #35 is
              // about, on a smaller scale.
              onChange({ ...value, country: e.target.value as CountryCode })
            }
          >
            {PHONE_COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.flag} {c.dial}
              </option>
            ))}
          </select>
          <span className="mucka-auth-cc-caret" aria-hidden="true">
            <Icon icon={ChevronDown} size={15} strokeWidth={2.5} />
          </span>
        </div>
        <div
          className={`mucka-auth-input-wrap mucka-auth-phone-input${error ? " mucka-auth-input-wrap--error" : ""}`}
        >
          <span className="mucka-auth-input-icon">
            <Icon icon={Phone} size={18} strokeWidth={2} />
          </span>
          <input
            className="mucka-auth-input"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={value.local}
            // reconcileTyped, not a bare setState: pasting a full +49 number
            // has to MOVE the chip, or the field shows one country and sends
            // another.
            onChange={(e) => onChange(reconcileTyped({ ...value, local: e.target.value }))}
            placeholder={country.placeholder}
            aria-label="Phone number"
          />
        </div>
      </div>
      {error && <div className="mucka-auth-field-error">{error}</div>}
    </div>
  );
}

export {
  buildFullPhone,
  validatePhone,
  splitE164,
  formatDisplayPhone,
  EMPTY_PHONE,
  type PhoneValue,
} from "./phone-countries";
